// Shared building blocks for the two watch wizards — the per-thread one in
// WatchesPanel.tsx and the org-level group one in
// routes/agents/orchestration/watches.tsx. Both poll-preview an MCP tool,
// build a selector by clicking the response tree, and (optionally) attach a
// sandboxed JS transform; keeping the pieces here guarantees the two
// surfaces behave identically (preview parity is a backend contract — the
// UI must not drift either).
import { useClient } from '@cortex/react-query';
import type {
  MCPConnection,
  PersistentAgentWatchPreviewTransform,
  PreviewPersistentAgentWatchResult,
} from '@cortex/types';
import { applyWatchSelector, parseWatchSelector, stableWatchStringify } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { childPath, generalizeIndices, isChurned, isSelectableKey } from './json-node-path';

import './watches-panel.css';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

/** Shape of `POST /v1/mcp-connections/:id/test-connection` (see mcp-connections.tsx). */
export interface ITestConnectionResult {
  ok: boolean;
  tools: Array<{ name: string; description?: string }>;
  error?: { code: string; message: string };
}

/**
 * Connections for the wizard's connection dropdown (fetched lazily — they
 * only matter while a wizard is open) plus the lazy tool-list probe. The
 * probe is the same test-connection call the Connections page uses; it also
 * writes `toolSchemaCache` server-side, so invalidating the list re-fetches
 * the populated cache.
 */
export function useWatchConnections(enabled: boolean) {
  const client = useClient();
  const qc = useQueryClient();
  const connections = useQuery({
    enabled,
    queryKey: ['mcp-connections', 'for-watches'],
    queryFn: () =>
      client.http.get('v1/mcp-connections?limit=200').json<IListEnvelope<MCPConnection>>(),
  });
  const loadTools = useMutation({
    mutationFn: (id: string) =>
      client.http.post(`v1/mcp-connections/${id}/test-connection`).json<ITestConnectionResult>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-connections'] }),
  });
  return { connections, loadTools };
}

/** Tool dropdown fed by the connection's cached schemas, with a lazy probe
 *  (the same test-connection call the Connections page uses) when the cache
 *  is empty. */
export function ToolField({
  connection,
  value,
  loadTools,
  onChange,
}: {
  connection: MCPConnection | null;
  value: string;
  loadTools: {
    mutate: (id: string) => void;
    isPending: boolean;
    variables: string | undefined;
    data: ITestConnectionResult | undefined;
  };
  onChange: (tool: string) => void;
}) {
  const cached = connection?.toolSchemaCache
    ? Object.keys(connection.toolSchemaCache.schemas).sort()
    : [];
  const probed =
    connection && loadTools.variables === connection.id && loadTools.data?.ok
      ? loadTools.data.tools.map((t) => t.name).sort()
      : [];
  const tools = cached.length > 0 ? cached : probed;
  const probeFailed =
    connection && loadTools.variables === connection.id && loadTools.data?.ok === false;

  return (
    <label className="field" style={{ flex: 1, minWidth: 180 }}>
      <span className="field-label">Tool</span>
      <select
        className="select-native"
        value={value}
        disabled={!connection || tools.length === 0}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{connection ? '— Select a tool —' : '— Pick a connection first —'}</option>
        {tools.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      {connection && tools.length === 0 && (
        <span className="field-help">
          No tools cached yet.{' '}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={loadTools.isPending}
            onClick={() => loadTools.mutate(connection.id)}
          >
            {loadTools.isPending ? 'Loading…' : 'Load tools'}
          </button>
          {probeFailed && (
            <span style={{ color: 'var(--status-danger)' }}>
              {' '}
              {loadTools.data?.error?.message ?? 'Connection test failed'}
            </span>
          )}
        </span>
      )}
    </label>
  );
}

/** The wizard's core: sample[0] as a clickable tree (click a node → selector),
 *  churn highlights, `[N]`→`[*]` generalization, and the live client-side
 *  projection with the across-samples stability verdict. */
export function PreviewExplorer({
  result,
  selector,
  selectorInvalid,
  lastGoodSelector,
  onSelectorChange,
}: {
  result: PreviewPersistentAgentWatchResult;
  selector: string;
  selectorInvalid: boolean;
  lastGoodSelector: string;
  onSelectorChange: (next: string) => void;
}) {
  const sample0 = result.samples[0];
  const sample1 = result.samples.length > 1 ? (result.samples[1] ?? null) : null;
  if (!sample0) return null;

  // Project with the last selector that parsed, so the preview never blanks
  // mid-keystroke; empty selector = identity (watch the whole response).
  const effective = selectorInvalid ? lastGoodSelector : selector.trim();
  const segments = effective === '' ? [] : (parseWatchSelector(effective) ?? []);
  const projected = applyWatchSelector(sample0.output, segments);
  const stable = sample1
    ? stableWatchStringify(projected) ===
      stableWatchStringify(applyWatchSelector(sample1.output, segments))
    : null;

  const fullText = JSON.stringify(projected, null, 2) ?? 'undefined';
  const projectedText = fullText.length > 4000 ? `${fullText.slice(0, 4000)}\n…` : fullText;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {result.churnedPaths.length > 0 && (
        <span className="t-xs" style={{ color: 'var(--status-warning)' }}>
          Highlighted fields changed between two back-to-back polls — exclude them from your
          selector.
        </span>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <label className="field" style={{ flex: 1 }}>
          <span className="field-label">Selector — click a node below, or type</span>
          <input
            className="input t-mono"
            value={selector}
            placeholder="empty = watch the whole response"
            onChange={(e) => onSelectorChange(e.target.value)}
          />
        </label>
        <button
          className="btn btn--ghost btn--sm"
          disabled={!/\[\d+\]/.test(selector)}
          title="Generalize: replace every [N] index with the [*] map"
          onClick={() => onSelectorChange(generalizeIndices(selector))}
        >
          Map arrays [N]→[*]
        </button>
      </div>

      <JsonTree
        value={sample0.output}
        churnedPaths={result.churnedPaths}
        onSelect={onSelectorChange}
      />

      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
          <span className="field-label">Projection — what the watch compares & stores</span>
          {selectorInvalid && (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              invalid selector
            </span>
          )}
          {!selectorInvalid && stable !== null && (
            <span
              className="t-xs"
              style={{ color: stable ? 'var(--status-success)' : 'var(--status-warning)' }}
            >
              {stable ? 'stable across samples ✓' : 'still churning ✗'}
            </span>
          )}
        </div>
        <pre
          className="t-mono t-xs"
          style={{
            margin: '4px 0 0',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--surface-1, rgba(255,255,255,0.02))',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
            maxHeight: 200,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            opacity: selectorInvalid ? 0.5 : 1,
          }}
        >
          {projectedText}
        </pre>
      </div>
    </div>
  );
}

// ─── Transform mode ──────────────────────────────────────────────────────

/** Seeded into the code editor's placeholder so an empty editor teaches the contract. */
export const transformPlaceholder = `// input.output = projected tool result
// // return <payload> | undefined`;

export interface ITransformFieldsValue {
  /** JS body run per poll in the sandbox; empty string = hash-on-change mode. */
  code: string;
  /** Raw textarea contents — parsed (as a JSON object) on use. */
  bindingsText: string;
  /** Raw text so partial input isn't coerced mid-keystroke; parsed on use. */
  timeoutMs: string;
}

export const emptyTransformFields: ITransformFieldsValue = {
  code: '',
  bindingsText: '',
  timeoutMs: '5000',
};

// TransformEditor (Monaco + templates + LLM assist) lives in
// watch-transform-editor.tsx — re-exported here so existing callers keep working.
export {
  TransformEditor,
  watchTransformTemplates,
  type IWatchTransformTemplate,
  type ITransformEditorProps,
} from './watch-transform-editor';

/**
 * Dry-run outcome of a preview-with-code: the transform's emissions (as
 * subject chips), its next state, or its error. Sits under the samples/churn
 * UI so the wizard reads top-down: raw output → projection → emissions.
 */
export function TransformResultPanel({
  transform,
}: {
  transform: PersistentAgentWatchPreviewTransform;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-sm, 4px)',
        padding: 'var(--sp-2) var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <span className="field-label">Map dry-run — first sample</span>
      {transform.error ? (
        <span className="t-sm" style={{ color: 'var(--status-danger)' }}>
          Map error: {transform.error}
        </span>
      ) : (
        <>
          <span className="t-xs t-muted">
            {transform.wouldWake
              ? 'Would wake on this sample.'
              : Array.isArray(transform.mapped) && transform.mapped.length === 0
                ? 'Mapped to an empty list — nothing matched the filter (or threshold). No wake if using “nonempty” / first-appear.'
                : 'Would not wake yet (same as last value, or gate not met). Mapped payload still shown below.'}
          </span>
          <pre
            className="t-mono t-xs"
            style={{
              margin: 0,
              maxHeight: 180,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {transform.mapped === undefined
              ? 'undefined (map skipped this poll)'
              : JSON.stringify(transform.mapped, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}

/** `type/id` subject chip — the lane suffix one emission writes to. */

// ─── Clickable JSON tree ─────────────────────────────────────────────────
// Mirrors common/JsonViewer's look but every key is a button that emits its
// selector-grammar path, and churned paths are highlighted. Kept local: the
// shared viewer is read-only and path-unaware.

const maxChildren = 50;

function JsonTree({
  value,
  churnedPaths,
  onSelect,
}: {
  value: unknown;
  churnedPaths: string[];
  onSelect: (path: string) => void;
}) {
  return (
    <div className="pa-watch-tree">
      <TreeNode
        label="$"
        path=""
        value={value}
        depth={0}
        selectable={true}
        churnedPaths={churnedPaths}
        onSelect={onSelect}
      />
    </div>
  );
}

function TreeNode({
  label,
  path,
  value,
  depth,
  selectable,
  churnedPaths,
  onSelect,
}: {
  label: string;
  path: string;
  value: unknown;
  depth: number;
  selectable: boolean;
  churnedPaths: string[];
  onSelect: (path: string) => void;
}) {
  // Deep levels start collapsed so huge outputs stay scannable.
  const [open, setOpen] = useState(depth < 2);
  const [showAll, setShowAll] = useState(false);
  const churned = isChurned(path, churnedPaths);

  const keyEl = selectable ? (
    <button
      type="button"
      className={`pa-watch-node${churned ? ' pa-watch-node--churned' : ''}`}
      title={
        churned
          ? 'Changed between back-to-back polls — selecting it means every poll looks like a change'
          : `Watch ${path === '' ? 'the whole response' : `"${path}"`}`
      }
      onClick={() => onSelect(path)}
    >
      {label}
    </button>
  ) : (
    <span className="t-muted" title="Key not expressible in the selector grammar">
      {label}
    </span>
  );

  const entries: Array<[string | number, unknown]> | null = Array.isArray(value)
    ? value.map((v, i) => [i, v])
    : value !== null && typeof value === 'object'
      ? Object.entries(value as Record<string, unknown>)
      : null;

  if (entries === null) {
    return (
      <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
        {keyEl}
        <span style={{ color: 'var(--text-muted)' }}>: </span>
        <Leaf value={value} />
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const summary = isArray
    ? `[ ${entries.length} item${entries.length === 1 ? '' : 's'} ]`
    : `{ ${entries.length} key${entries.length === 1 ? '' : 's'} }`;
  const shown = showAll ? entries : entries.slice(0, maxChildren);

  return (
    <div style={{ paddingLeft: depth === 0 ? 0 : 12 }}>
      <button
        type="button"
        aria-label={open ? 'Collapse' : 'Expand'}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '0 4px 0 0',
          fontSize: 10,
          lineHeight: 1,
        }}
      >
        {open ? '▾' : '▸'}
      </button>
      {keyEl}
      <span style={{ color: 'var(--text-muted)' }}>
        {entries.length === 0
          ? isArray
            ? ' [ ]'
            : ' { }'
          : open
            ? isArray
              ? ' ['
              : ' {'
            : ` ${summary}`}
      </span>
      {open && entries.length > 0 && (
        <>
          {shown.map(([k, v]) => (
            <TreeNode
              key={String(k)}
              label={typeof k === 'number' ? `[${k}]` : k}
              path={isSelectableKey(k) ? childPath(path, k) : path}
              value={v}
              depth={depth + 1}
              selectable={isSelectableKey(k)}
              churnedPaths={churnedPaths}
              onSelect={onSelect}
            />
          ))}
          {entries.length > shown.length && (
            <div style={{ paddingLeft: 12 }}>
              <button
                type="button"
                className="pa-watch-node"
                style={{ fontWeight: 400 }}
                onClick={() => setShowAll(true)}
              >
                … {entries.length - shown.length} more
              </button>
            </div>
          )}
          <div>
            <span style={{ color: 'var(--text-muted)' }}>{isArray ? ']' : '}'}</span>
          </div>
        </>
      )}
    </div>
  );
}

/** Primitive value token — same color scheme as common/JsonViewer. */
function Leaf({ value }: { value: unknown }) {
  if (value === null) return <span style={{ color: 'var(--text-muted)' }}>null</span>;
  if (typeof value === 'string') {
    const text = value.length > 120 ? `${value.slice(0, 120)}…` : value;
    return <span style={{ color: 'var(--accent-secondary, #88d39a)' }}>&quot;{text}&quot;</span>;
  }
  if (typeof value === 'number' || typeof value === 'boolean')
    return <span style={{ color: 'var(--accent-primary, #6ea8ff)' }}>{String(value)}</span>;
  return <span style={{ color: 'var(--text-muted)' }}>{String(value)}</span>;
}
